// Microsoft Clarity (session recording + heatmaps).
//
// Previously injected by Cloudflare Zaraz. Loading it here instead keeps every
// analytics tool in code — same behaviour in dev, preview and production, and
// no silent drift when a dashboard toggle changes. This mirrors Clarity's
// official snippet:
//
//   1. `window.clarity` is installed synchronously as a queue stub, so
//      trackEvent() (composables/analytics.ts) can fire events before the real
//      tag has arrived — Clarity flushes `clarity.q` once it loads, so no
//      event is lost. This is the same buffering trick window.dataLayer does
//      for gtag; see plugins/gtag.client.ts.
//   2. The tag itself is fetched on idle rather than during the critical
//      render, keeping third-party JS off the first-paint path.
//
// Note the asymmetry with GA: API calls are buffered, but DOM activity is NOT.
// The recorder only starts capturing once the tag loads, so the idle timeout is
// deliberately short (2s, vs 4s for GA) to keep the blind spot at the start of
// each recording small. If you'd rather capture from the very first paint, drop
// the requestIdleCallback wrapper and call load() directly — that trades some
// page speed for complete recordings.
export default defineNuxtPlugin(() => {
  const id = useRuntimeConfig().public?.clarityId
  // No project id configured (e.g. local dev) → nothing to load.
  if (!id) return

  const w = window as any
  // Already present — e.g. Zaraz re-enabled in the Cloudflare dashboard, or a
  // HMR reload re-running this plugin. Loading Clarity twice double-counts
  // sessions, so bail out rather than stack a second tag.
  if (w.clarity) return

  w.clarity = function () {
    ;(w.clarity.q = w.clarity.q || []).push(arguments)
  }

  const load = () => {
    try {
      const script = document.createElement('script')
      script.async = true
      script.src = `https://www.clarity.ms/tag/${id}`
      document.head.appendChild(script)
    } catch {
      // Analytics is best-effort — never let a Clarity load failure surface as
      // a page error.
    }
  }

  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(load, { timeout: 2000 })
  } else {
    setTimeout(load, 1500)
  }
})
